import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef } from "react";
import { useTheme } from "@/app/providers/ThemeContext";
import { handleLandingAnchorClick } from "@/landing/landingScroll";

gsap.registerPlugin(ScrollTrigger);

const productLinks = [
    { label: "How It Works", href: "#tools" },
    { label: "Options Intelligence", href: "#tools" },
    { label: "Volatility Analysis", href: "#tools" },
    { label: "Oracle Model", href: "#tools" },
    { label: "Pricing", href: "#plans" },
];

const planLinks = [
    { label: "Free Trial", href: "#plans" },
    { label: "Toolbox  $18/mo", href: "#plans" },
    { label: "Oracle — $29/mo", href: "#plans" },
    { label: "Oracle Monitor — $79/mo", href: "#plans" },
];

const alertLabels = ["Slack", "Telegram", "Email", "Webhooks"];

const useCaseLinks = [
    { label: "Contract Entry Timing", href: "#tools" },
    { label: "Strike & Expiry Selection", href: "#tools" },
    { label: "Volatility Regime Shifts", href: "#tools" },
    { label: "Position Management", href: "#tools" },
    { label: "Event-Driven Setup", href: "#tools" },
    { label: "Flow Confirmation", href: "#tools" },
];

function FooterLinkGroup({ title, links, isDark }) {
    return (
        <div>
            <div style={{
                fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                textTransform: "uppercase", letterSpacing: "0.14em",
                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.4)", marginBottom: "14px",
            }}>
                {title}
            </div>
            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "2px" }}>
                {links.map((l) => (
                    <li key={l.label}>
                        <a
                            href={l.href}
                            onClick={(e) => handleLandingAnchorClick(e, l.href)}
                            style={{
                                fontSize: "0.78rem", color: isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)",
                                textDecoration: "none", transition: "color 0.2s",
                                display: "block", padding: "10px 0", minHeight: "44px",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "white" : "#111111"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)"; }}
                        >
                            {l.label}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Footer() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const footerRef = useRef(null);
    const col1Ref = useRef(null);
    const colsRef = useRef([]);
    const botRef = useRef(null);
    const riskRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {

            // ── Logo col + link columns stagger in ───────────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: footerRef.current, start: "top 88%", toggleActions: "play none none none" },
            })
                .fromTo(col1Ref.current,
                    { opacity: 0, y: 24 },
                    { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" }
                )
                .fromTo(
                    colsRef.current.filter(Boolean),
                    { opacity: 0, y: 20 },
                    { opacity: 1, y: 0, duration: 0.45, stagger: 0.08, ease: "power2.out" },
                    "-=0.3"
                );

            // ── Bottom bar slides up ──────────────────────────────────────────
            gsap.fromTo(botRef.current,
                { opacity: 0, y: 12 },
                {
                    opacity: 1, y: 0, duration: 0.4, ease: "power2.out",
                    scrollTrigger: { trigger: botRef.current, start: "top 95%", toggleActions: "play none none none" },
                }
            );

            // ── Risk disclosure fades in last ─────────────────────────────────
            gsap.fromTo(riskRef.current,
                { opacity: 0 },
                {
                    opacity: 1, duration: 0.5, ease: "power1.out",
                    scrollTrigger: { trigger: riskRef.current, start: "top 98%", toggleActions: "play none none none" },
                }
            );

        }, footerRef);

        return () => ctx.revert();
    }, []);

    const linkStyle = {
        fontSize: "0.78rem", color: isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)",
        textDecoration: "none", transition: "color 0.2s",
        display: "inline-block", padding: "10px 0", minHeight: "44px",
    };

    return (
        <footer data-testid="footer" ref={footerRef} style={{ background: isDark ? "#0d0d0d" : "#ffffff", borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #c4c2bc" }}>

            {/* ── Main grid ────────────────────────────────────────────────── */}
            <div
                className="grid grid-cols-2 md:grid-cols-[200px_repeat(3,1fr)_220px] gap-8 md:gap-9"
                style={{
                    maxWidth: "1200px", margin: "0 auto", padding: "56px 24px 36px",
                }}
            >

                {/* Col 1 — Brand */}
                <div ref={col1Ref} style={{ opacity: 0 }}>
                    <a
                        href="/"
                        style={{
                            fontFamily: "var(--font-display)", fontSize: "1.05rem",
                            color: isDark ? "white" : "#111111", letterSpacing: "0.04em",
                            display: "flex", alignItems: "center", gap: "8px",
                            marginBottom: "14px", textDecoration: "none",
                        }}
                    >
                        <div style={{
                            width: "28px", height: "28px", borderRadius: "6px",
                            background: "#00B8DB", display: "grid", placeItems: "center",
                            fontSize: "0.65rem", fontFamily: "var(--font-mono)",
                            color: isDark ? "white" : "#111111", fontWeight: 700, letterSpacing: "0.02em",
                        }}>
                            LH
                        </div>
                        LinkedHash
                    </a>
                    <p style={{
                        fontSize: "0.7rem", color: isDark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.5)",
                        lineHeight: 1.6, fontStyle: "italic", marginBottom: "18px",
                    }}>
                        Live Market Data. Intelligence. Analysis &amp; Alerts — powered by Claude.
                    </p>
                    <a
                        href="#plans"
                        onClick={(e) => handleLandingAnchorClick(e, "#plans")}
                        style={{
                            fontFamily: "var(--font-mono)", fontSize: "0.65rem",
                            padding: "8px 16px", border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.3)",
                            borderRadius: "100px", color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.7)",
                            background: "transparent", cursor: "pointer",
                            letterSpacing: "0.04em", textDecoration: "none",
                            display: "inline-block", transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = isDark ? "white" : "#111111";
                            e.currentTarget.style.color = isDark ? "white" : "#111111";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.3)";
                            e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.7)";
                        }}
                    >
                        Start Free Trial →
                    </a>
                </div>

                {/* Col 2 — Product */}
                <div ref={(el) => { colsRef.current[0] = el; }} style={{ opacity: 0 }}>
                    <FooterLinkGroup title="Product" links={productLinks} isDark={isDark} />
                </div>

                {/* Col 3 — Plans + Alert Channels */}
                <div ref={(el) => { colsRef.current[1] = el; }} style={{ opacity: 0 }}>
                    <FooterLinkGroup title="Plans" links={planLinks} isDark={isDark} />
                    <div style={{ marginTop: "18px" }}>
                        <div>
                            <div style={{
                                fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                                textTransform: "uppercase", letterSpacing: "0.14em",
                                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.4)", marginBottom: "14px",
                            }}>
                                Alert Channels
                            </div>
                            <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "9px" }}>
                                {alertLabels.map((label) => (
                                    <li key={label}>
                                        <span
                                            style={{
                                                fontSize: "0.78rem", color: isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)",
                                            }}
                                        >
                                            {label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Col 4 — Use Cases */}
                <div ref={(el) => { colsRef.current[2] = el; }} style={{ opacity: 0 }}>
                    <FooterLinkGroup title="Use Cases" links={useCaseLinks} isDark={isDark} />
                </div>

                {/* Col 5 — Support */}
                <div ref={(el) => { colsRef.current[3] = el; }} style={{ opacity: 0 }}>
                    <div style={{
                        fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                        textTransform: "uppercase", letterSpacing: "0.14em",
                        color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.4)", marginBottom: "14px",
                    }}>
                        Support
                    </div>
                    <div style={{ fontSize: "0.73rem", color: isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.5)", lineHeight: 1.7 }}>
                        {[
                            { label: "Community", node: <a href="https://github.com/linkedhash01/lh-agents/discussions" target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, color: isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)" }} onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "white" : "#111111"; }} onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)"; }}>GitHub Discussions</a> },
                            { label: "Email Support", text: "Typically under 48 hours (Oracle)\nUnder 24 hours (Oracle Monitor)" },
                            {
                                label: "Documentation", node: <a href="#" style={{ ...linkStyle, color: isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)" }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "white" : "#111111"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.52)" : "rgba(0,0,0,0.6)"; }}
                                >docs.linkedhash.io</a>
                            },
                        ].map((item) => (
                            <div key={item.label}>
                                <strong style={{
                                    display: "block", fontFamily: "var(--font-mono)",
                                    fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.09em",
                                    color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.7)", marginBottom: "3px", marginTop: "12px",
                                    fontWeight: 600,
                                }}>
                                    {item.label}
                                </strong>
                                {item.node ?? item.text.split("\n").map((t, i) => <span key={i}>{t}{i === 0 && item.text.includes("\n") && <br />}</span>)}
                            </div>
                        ))}

                        <strong style={{
                            display: "block", fontFamily: "var(--font-mono)",
                            fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.09em",
                            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.7)", marginBottom: "3px", marginTop: "12px", fontWeight: 600,
                        }}>
                            Follow Us
                        </strong>
                        <div className="flex gap-1 mt-2">
                            {[
                                { key: "x", href: "#", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                                { key: "linkedin", href: "#", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                                { key: "youtube", href: "#", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
                            ].map((s) => (
                                <a
                                    key={s.key}
                                    href={s.href}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    style={{ color: isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.5)", fontSize: "0.78rem", textDecoration: "none", transition: "color 0.2s", display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px" }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "white" : "#111111"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.5)"; }}
                                >
                                    {s.icon}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Bottom bar ───────────────────────────────────────────────── */}
            <div
                ref={botRef}
                className="flex items-center justify-between flex-wrap gap-2"
                style={{
                    maxWidth: "1200px", margin: "0 auto", padding: "16px 24px 20px",
                    borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #c4c2bc", opacity: 0,
                }}
            >
                <span style={{
                    fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                    color: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.4)", letterSpacing: "0.07em", textTransform: "uppercase",
                }}>
                    COPYRIGHT 2026 © LINKEDHASH
                </span>
                <div className="flex flex-wrap gap-1">
                    {["Privacy Policy", "Terms of Service", "Cookies Policy"].map((l) => (
                        <a
                            key={l}
                            href="/privacy"
                            style={{
                                fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                                color: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.4)", textDecoration: "none", transition: "color 0.2s",
                                padding: "10px 8px", display: "inline-block", minHeight: "44px", lineHeight: "24px",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.7)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.4)"; }}
                        >
                            {l}
                        </a>
                    ))}
                </div>
                <a
                    href="#hero"
                    className="flex items-center gap-1"
                    onClick={(e) => handleLandingAnchorClick(e, "#hero")}
                    style={{
                        fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                        color: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.4)", textDecoration: "none",
                        letterSpacing: "0.07em", textTransform: "uppercase", transition: "color 0.2s",
                        padding: "10px 4px", minHeight: "44px", lineHeight: "24px",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.7)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.4)"; }}
                >
                    Back to the Top ↑
                </a>
            </div>

            {/* ── Risk disclosure ──────────────────────────────────────────── */}
            <div
                ref={riskRef}
                style={{
                    maxWidth: "1200px", margin: "0 auto", padding: "20px 24px 36px",
                    fontSize: "0.66rem", color: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.35)",
                    lineHeight: 1.75,
                    borderTop: isDark ? "1px solid rgba(255,255,255,0.03)" : "1px solid #c4c2bc",
                    opacity: 0,
                }}
            >
                <strong style={{
                    display: "block", fontFamily: "var(--font-mono)",
                    fontSize: "0.58rem", textTransform: "uppercase", letterSpacing: "0.1em",
                    marginBottom: "7px", color: isDark ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.5)",
                }}>
                    Risk Disclosure
                </strong>
                Options trading involves significant risk and is not suitable for all investors. The analysis and alerts
                provided by LinkedHash are for informational purposes only and do not constitute financial advice. Past
                performance of any strategy or indicator does not guarantee future results. LinkedHash does not manage
                money, execute trades, or provide investment advisory services. Always consult a licensed financial
                professional before making investment decisions. Trading with leverage can result in losses that exceed your
                initial deposit.
            </div>

        </footer>
    );
}
